import React, { useRef, useState } from 'react';
import { Button, Dropdown, FormControl, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useClues, useCursor, useSlotStructure } from './BoardInteractionContext';
import { ACROSS, DOWN } from '../../libs/directionsLib';
import { FaLongArrowAltDown } from 'react-icons/fa';
import { expandSlotName, condenseSlotName, removeClueCommands, removeClueCommand, extractClueCommands } from '../../libs/puzzleLib';
import { HiOutlineTrash } from 'react-icons/hi';
import { TbRepeat, TbRepeatOff } from 'react-icons/tb';





function ClueDisplay({ slotName, word, clueText, ...props }) {
  return (
    <div {...props}>
      <div className='d-flex align-items-center gap-2'>
        <div className='d-flex block-text' style={{ border: 'solid #333333 1.5px' }}>
          <div className='px-2 py-1 h-100 grey-1-bkgd' style={{ borderRight: 'solid #333333 1px' }}>{condenseSlotName(slotName)}</div>
          <div className='px-2 py-1 h-100'>{word}</div>
        </div>

        <div className='small'>{ removeClueCommands(clueText) }</div>
      </div>
    </div>
  );
}




export default function ClueLinkageModal({ show, onHide, ...props }) {

  const { clues, setCluesAt } = useClues();
  const { currentSlotName } = useCursor();
  const slotStructure = useSlotStructure();

  const searchInputRef = useRef(null);
  const [searchToken, setSearchToken] = useState('');

  if (!currentSlotName) return null;

  const clueText = clues.get(currentSlotName) || '';
  const linkedSlotNames = extractClueCommands(clueText, /^link:\d+[AD]$/).map(cc => expandSlotName(cc.slice(5))).sort((a, b) => {
    // If multiple linked slot names in this clue, sort into the expected clue order (Across, then Down; ascending numbers)
    if (a.endsWith(ACROSS) && b.endsWith(DOWN)) return -1;
    if (a.endsWith(DOWN) && b.endsWith(ACROSS)) return 1;
    return parseInt(a) - parseInt(b);
  });

  function addLinkedSlot(rootSlotName, linkedSlotName) {
    // Add this slotName as a linked clue
    if (clues.get(rootSlotName) !== undefined && !clues.get(rootSlotName).includes(`$link:${condenseSlotName(linkedSlotName)}$`)) {
      setCluesAt([{
        slotName: rootSlotName,
        clueText: clues.get(rootSlotName) + `$link:${condenseSlotName(linkedSlotName)}$`
      }]);
    }
  }

  // TODO: troubleshoot edge cases of removeClueCommand
  function removeLinkedSlot(rootSlotName, linkedSlotName) {
    if (clues.get(rootSlotName)) {
      setCluesAt([{
        slotName: rootSlotName,
        clueText: removeClueCommand(clues.get(rootSlotName), `link:${condenseSlotName(linkedSlotName)}`),
      }]);
    }
  }

  // Calculate other clue options to link to; filter out current clue itself, and any already-linked clues; also take into account any searchToken for filtering
  const filteredDropdownItems = [...clues].filter(([slotName, clueText]) => {
    if (slotName === currentSlotName || linkedSlotNames.includes(slotName)) return false;
    const possibleMatches = [condenseSlotName(slotName), slotName, slotStructure.getWordInSlotName(slotName), clueText];
    return possibleMatches.some(str => str.toLowerCase().includes(searchToken.toLowerCase()));   // include the item if it matches to any substring of the possible matches (slotName, condensed slotName, word, clueText)
  }).map(([slotName, clueText]) => (
    <Dropdown.Item key={slotName} onClick={() => addLinkedSlot(currentSlotName, slotName)}>
      <ClueDisplay slotName={slotName} word={slotStructure.getWordInSlotName(slotName, '·')} clueText={clueText} style={{ maxWidth: '300px', overflow: 'hidden' }} />
    </Dropdown.Item>
  ));
  

  return (
    <Modal show={show} onHide={onHide} {...props}>
      <Modal.Header className='purple-2-bkgd' closeButton onClick={e => e.stopPropagation()}><strong>Link clues</strong></Modal.Header>
      <Modal.Body onClick={e => e.stopPropagation()}>
        <div className='d-flex flex-column gap-3 align-items-center'>
          <div className='mb-1 small balanced-text text-center fw-bold'>Select other words that should also highlight in association with this word.</div>
          
          <ClueDisplay slotName={currentSlotName} word={slotStructure.getWordInSlotName(currentSlotName, '·')} clueText={clueText} />

          {linkedSlotNames.length === 0 ? (
            <div className='small fst-italic'>This clue doesn't link to others.</div>
          ) : (
            <>
              <div className='d-flex gap-2 align-items-center'><FaLongArrowAltDown size={30} /><span className='small fst-italic'>also highlights</span></div>
              <div className='d-flex flex-column gap-2 align-items-start'>
                {linkedSlotNames.map((slotName, idx) => {

                  // Is the reverse linkage also true?
                  const isBidirectional = clues.get(slotName)?.includes(`$link:${condenseSlotName(currentSlotName)}$`);

                  return <div className='d-flex align-items-center'>
                    <OverlayTrigger
                      overlay={<Tooltip>Remove this linkage</Tooltip>}
                    >
                      <Button
                        className='p-2 d-flex align-items-center border-0'
                        variant='outline-danger'
                        onClick={() => removeLinkedSlot(currentSlotName, slotName)}
                      ><HiOutlineTrash /></Button>
                    </OverlayTrigger>

                    <OverlayTrigger overlay={<Tooltip>{isBidirectional ? 'Remove reverse link' : 'Link reverse direction too'}</Tooltip>}>
                      <Button
                        className='p-2 d-flex align-items-center border-0 me-2'
                        variant='outline-info'
                        onClick={() => {
                          if (isBidirectional) {
                            // Unlink the reverse direction
                            removeLinkedSlot(slotName, currentSlotName);
                          } else {
                            // Add reverse link
                            addLinkedSlot(slotName, currentSlotName);
                          }
                        }}
                      >{isBidirectional ? <TbRepeatOff /> : <TbRepeat />}</Button>
                    </OverlayTrigger>

                    <ClueDisplay slotName={slotName} word={slotStructure.getWordInSlotName(slotName, '·')} clueText={clues.get(slotName)} key={`linked-clue-${idx}`} />
                  </div>;
                })}
              </div>
            </>
          )}

          <div className='d-flex gap-5'>
            <Dropdown onToggle={(isOpen) => {
              if (isOpen) {
                searchInputRef.current?.focus();
              }
            }}>
              <Dropdown.Toggle
                variant='info'
              >
                Add linked clue
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <FormControl
                  className='mx-auto mb-2'
                  ref={searchInputRef}
                  style={{ width: '90%' }}
                  autoFocus
                  value={searchToken}
                  onChange={e => setSearchToken(e.target.value)}
                  size='sm'
                  placeholder='search by number, word, or clue text'
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filteredDropdownItems.length >= 1) {
                      addLinkedSlot(currentSlotName, filteredDropdownItems[0].props.children.props.slotName);
                    }
                  }}
                />

                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {filteredDropdownItems}
                </div>

                {filteredDropdownItems.length === 0 && <div className='ms-2 mt-1 fst-italic small text-muted'>No results.</div>}
              </Dropdown.Menu>
            </Dropdown>

            <Button onClick={() => onHide()} variant='outline-info'>Done</Button>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  )
}